import { Stack } from '@mui/material';
import { useIntl } from 'react-intl';
import { RedoIcon, UndoIcon } from '~/assets/workspace-icons';
import ToolbarButton from '~/components/toolbar/shared/ToolbarButton';
import { buildToolTooltip } from '~/components/toolbar/shared/toolTooltip';
import { useRegistrationUndoRedo } from '~/hooks/undo/useRegistrationUndoRedo';

interface RegistrationUndoRedoToolProps {
    isDisabled: boolean;
    /** Why undo and redo are unavailable, appended to their tooltips while disabled. */
    disabledReason?: string;
}

export default function RegistrationUndoRedoTool({
    isDisabled,
    disabledReason,
}: RegistrationUndoRedoToolProps) {
    const intl = useIntl();
    const { canUndo, canRedo, undo, redo } = useRegistrationUndoRedo();
    const undoLabel = intl.formatMessage({ id: 'registration.undo' });
    const redoLabel = intl.formatMessage({ id: 'registration.redo' });
    const reason = isDisabled ? disabledReason : undefined;

    return (
        <Stack direction="row" alignItems="center">
            <ToolbarButton
                icon={<UndoIcon />}
                tooltip={
                    reason
                        ? buildToolTooltip(intl, undoLabel, reason)
                        : canUndo
                          ? undoLabel
                          : intl.formatMessage({ id: 'toolbar.undo.nothing' })
                }
                ariaLabel={undoLabel}
                disabled={!canUndo || isDisabled}
                onClick={undo}
                data-testid="registration-undo"
            />
            <ToolbarButton
                icon={<RedoIcon />}
                tooltip={
                    reason
                        ? buildToolTooltip(intl, redoLabel, reason)
                        : canRedo
                          ? redoLabel
                          : intl.formatMessage({ id: 'toolbar.redo.nothing' })
                }
                ariaLabel={redoLabel}
                disabled={!canRedo || isDisabled}
                onClick={redo}
                data-testid="registration-redo"
            />
        </Stack>
    );
}
